import { OrderRecommendation } from '../../services/item-details.models';

export const RECOMMENDATION_OVERLAP_SOURCES = new Set(['COPY', 'EXCEL']);

/** Stored on order_lines.recommendation_was_recommended (VARCHAR). */
export type RecommendationWasRecommendedValue = 'true' | 'false' | 'NO_RECO' | null;

export const RECO_WAS_TRUE = 'true' as const;
export const RECO_WAS_FALSE = 'false' as const;
export const RECO_WAS_NO_RECO = 'NO_RECO' as const;

export type RecommendationOverlapLine = {
    itemNumber?: string | null;
    itemNo?: string | null;
    recommendationAddSource?: string | null;
    recommendationWasRecommended?: RecommendationWasRecommendedValue | boolean | null;
    recommendationRank?: number | null;
    recommendationRunId?: string | null;
};

/**
 * Normalize boolean/string legacy values to canonical storage form.
 */
export function normalizeRecommendationWasRecommended(
    raw: unknown
): RecommendationWasRecommendedValue {
    if (raw === true || raw === 'true' || raw === '1' || raw === 1) {
        return RECO_WAS_TRUE;
    }
    if (raw === false || raw === 'false' || raw === '0' || raw === 0) {
        return RECO_WAS_FALSE;
    }
    if (typeof raw === 'string' && raw.trim().toUpperCase() === 'NO_RECO') {
        return RECO_WAS_NO_RECO;
    }
    return null;
}

/**
 * Sets was_recommended / rank / run_id like MANUAL without changing add_source.
 * Empty recommendations list (completed fetch with none) → NO_RECO.
 * Match → true; non-empty list no match → false.
 */
export function enrichRecommendationOverlap(
    line: RecommendationOverlapLine,
    recByItemId: Map<string, OrderRecommendation>,
    runId: string | null,
    recommendationsAvailable: boolean | null = true
): void {
    if (!line) {
        return;
    }
    const source = String(line.recommendationAddSource ?? '').trim();
    if (!RECOMMENDATION_OVERLAP_SOURCES.has(source)) {
        return;
    }
    const itemId = String(line.itemNumber ?? line.itemNo ?? '').trim();
    if (!itemId) {
        return;
    }
    // Completed fetch with no recommendations for this customer
    if (recommendationsAvailable === false || (recommendationsAvailable !== null && recByItemId.size === 0)) {
        line.recommendationWasRecommended = RECO_WAS_NO_RECO;
        line.recommendationRank = null;
        line.recommendationRunId = null;
        return;
    }
    // Still loading / unknown — do not invent false
    if (recommendationsAvailable === null) {
        return;
    }
    const match = recByItemId.get(itemId);
    if (match) {
        line.recommendationWasRecommended = RECO_WAS_TRUE;
        line.recommendationRank =
            match.rank != null && Number.isFinite(Number(match.rank)) ? Number(match.rank) : null;
        line.recommendationRunId = runId;
    } else {
        line.recommendationWasRecommended = RECO_WAS_FALSE;
        line.recommendationRank = null;
        line.recommendationRunId = null;
    }
}

export function buildRecommendationByItemId(
    recommendations: OrderRecommendation[] | null | undefined
): Map<string, OrderRecommendation> {
    const map = new Map<string, OrderRecommendation>();
    for (const rec of recommendations || []) {
        const id = String(rec?.itemId ?? '').trim();
        if (id && !map.has(id)) {
            map.set(id, rec);
        }
    }
    return map;
}

/**
 * In-place enrich for COPY/EXCEL lines against a full recommendations list (pre-exclude).
 * Pass recommendationsAvailable=false when fetch completed with an empty list.
 */
export function enrichLinesWithRecommendationOverlap(
    lines: RecommendationOverlapLine[] | null | undefined,
    recommendations: OrderRecommendation[] | null | undefined,
    runId: string | null,
    recommendationsAvailable: boolean | null = true
): void {
    const list = lines || [];
    if (!list.length) {
        return;
    }
    const recByItemId = buildRecommendationByItemId(recommendations);
    const available =
        recommendationsAvailable === null
            ? null
            : recommendationsAvailable && recByItemId.size > 0
              ? true
              : recommendationsAvailable === false || recByItemId.size === 0
                ? false
                : true;
    for (const line of list) {
        enrichRecommendationOverlap(line, recByItemId, runId, available);
    }
}
